import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, TrendingUp, Users, Activity, MessageSquare, Video, Brain, LayoutDashboard, ChevronDown, ChevronUp } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface FeatureMetric {
  feature_name: string;
  total_usage: number;
  unique_users: number;
  last_used: string | null;
}

interface UserUsageData {
  user_id: string;
  email: string | null;
  total_usage: number;
  features: { feature_name: string; usage_count: number }[];
  quartile: number;
}

interface QuartileGroup {
  label: string;
  description: string;
  users: UserUsageData[];
  color: string;
}

const FEATURE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  'coach_dashboard_visit': { label: 'Dashboard Visits', icon: LayoutDashboard, color: 'text-blue-500' },
  'ai_analysis': { label: 'AI Analysis', icon: Brain, color: 'text-purple-500' },
  'video_call_started': { label: 'Video Calls', icon: Video, color: 'text-green-500' },
  'chat_opened': { label: 'Chat Sessions', icon: MessageSquare, color: 'text-orange-500' },
  'chat_open': { label: 'Chat (Legacy)', icon: MessageSquare, color: 'text-muted-foreground' },
  'coach_chat_open': { label: 'Coach Chat (Legacy)', icon: MessageSquare, color: 'text-muted-foreground' },
};

function getFeatureConfig(featureName: string) {
  return FEATURE_CONFIG[featureName] || {
    label: featureName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    icon: Activity,
    color: 'text-muted-foreground',
  };
}

export function FeatureMetricsPanel() {
  const [metrics, setMetrics] = useState<FeatureMetric[]>([]);
  const [userUsageData, setUserUsageData] = useState<UserUsageData[]>([]);
  const [quartiles, setQuartiles] = useState<QuartileGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedQuartiles, setExpandedQuartiles] = useState<Record<number, boolean>>({ 0: true });

  useEffect(() => {
    async function fetchMetrics() {
      try {
        setLoading(true);
        
        // Fetch all feature metrics
        const { data, error } = await supabase
          .from('feature_metrics')
          .select('feature_name, usage_count, user_id, last_used');

        if (error) throw error;

        // Aggregate by feature for overview
        const aggregatedByFeature: Record<string, FeatureMetric> = {};
        
        data?.forEach((row: any) => {
          const name = row.feature_name;
          if (!aggregatedByFeature[name]) {
            aggregatedByFeature[name] = {
              feature_name: name,
              total_usage: 0,
              unique_users: 0,
              last_used: null,
            };
          }
          aggregatedByFeature[name].total_usage += row.usage_count || 0;
          aggregatedByFeature[name].unique_users += 1;
          if (!aggregatedByFeature[name].last_used || (row.last_used && row.last_used > aggregatedByFeature[name].last_used)) {
            aggregatedByFeature[name].last_used = row.last_used;
          }
        });

        const sortedMetrics = Object.values(aggregatedByFeature).sort((a, b) => b.total_usage - a.total_usage);
        setMetrics(sortedMetrics);

        // Aggregate by user for quartile analysis
        const userMap: Record<string, { total: number; features: Record<string, number> }> = {};
        
        data?.forEach((row: any) => {
          if (!row.user_id) return;
          if (!userMap[row.user_id]) {
            userMap[row.user_id] = { total: 0, features: {} };
          }
          userMap[row.user_id].total += row.usage_count || 0;
          userMap[row.user_id].features[row.feature_name] = (userMap[row.user_id].features[row.feature_name] || 0) + (row.usage_count || 0);
        });

        // Fetch user emails
        const userIds = Object.keys(userMap);
        let emailMap: Record<string, string> = {};
        
        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('auth_user_id, email')
            .in('auth_user_id', userIds);
          
          profiles?.forEach((p: any) => {
            if (p.auth_user_id && p.email) {
              emailMap[p.auth_user_id] = p.email;
            }
          });
        }

        // Build user usage array
        const users: UserUsageData[] = Object.entries(userMap).map(([userId, data]) => ({
          user_id: userId,
          email: emailMap[userId] || null,
          total_usage: data.total,
          features: Object.entries(data.features)
            .map(([name, count]) => ({ feature_name: name, usage_count: count }))
            .sort((a, b) => b.usage_count - a.usage_count),
          quartile: 0,
        })).sort((a, b) => b.total_usage - a.total_usage);

        // Calculate quartiles
        if (users.length > 0) {
          const q1Index = Math.floor(users.length * 0.25);
          const q2Index = Math.floor(users.length * 0.5);
          const q3Index = Math.floor(users.length * 0.75);

          users.forEach((user, index) => {
            if (index < q1Index) user.quartile = 1;
            else if (index < q2Index) user.quartile = 2;
            else if (index < q3Index) user.quartile = 3;
            else user.quartile = 4;
          });

          const quartileGroups: QuartileGroup[] = [
            { 
              label: 'Power Users (Top 25%)', 
              description: 'Highest engagement',
              users: users.filter(u => u.quartile === 1),
              color: 'bg-green-500'
            },
            { 
              label: 'Active Users (25-50%)', 
              description: 'Above average usage',
              users: users.filter(u => u.quartile === 2),
              color: 'bg-blue-500'
            },
            { 
              label: 'Moderate Users (50-75%)', 
              description: 'Average engagement',
              users: users.filter(u => u.quartile === 3),
              color: 'bg-yellow-500'
            },
            { 
              label: 'Light Users (Bottom 25%)', 
              description: 'Low engagement',
              users: users.filter(u => u.quartile === 4),
              color: 'bg-muted-foreground'
            },
          ];

          setQuartiles(quartileGroups);
        }

        setUserUsageData(users);
      } catch (err: any) {
        console.error('Error fetching feature metrics:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchMetrics();
  }, []);

  const toggleQuartile = (index: number) => {
    setExpandedQuartiles(prev => ({ ...prev, [index]: !prev[index] }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4 text-destructive">
        Error loading metrics: {error}
      </div>
    );
  }

  const maxUsage = Math.max(...metrics.map(m => m.total_usage), 1);
  const totalUsage = metrics.reduce((sum, m) => sum + m.total_usage, 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Activity className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Actions</p>
                <p className="text-2xl font-bold">{totalUsage.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Features Tracked</p>
                <p className="text-2xl font-bold">{metrics.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Users</p>
                <p className="text-2xl font-bold">{userUsageData.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feature Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Feature Usage Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {metrics.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No feature usage data yet</p>
          ) : (
            metrics.map((metric) => {
              const config = getFeatureConfig(metric.feature_name);
              const Icon = config.icon;
              const percentage = (metric.total_usage / maxUsage) * 100;

              return (
                <div key={metric.feature_name} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${config.color}`} />
                      <span className="text-sm font-medium">{config.label}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        {metric.unique_users} users
                      </span>
                      <span className="font-semibold tabular-nums">
                        {metric.total_usage.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Quartile Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">User Quartile Distribution</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {quartiles.map((quartile, index) => (
            <Collapsible 
              key={index} 
              open={expandedQuartiles[index]} 
              onOpenChange={() => toggleQuartile(index)}
            >
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${quartile.color}`} />
                    <div className="text-left">
                      <p className="font-medium text-sm">{quartile.label}</p>
                      <p className="text-xs text-muted-foreground">{quartile.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary">{quartile.users.length} users</Badge>
                    {expandedQuartiles[index] ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 space-y-2 pl-6">
                  {quartile.users.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">No users in this quartile</p>
                  ) : (
                    quartile.users.map((user) => (
                      <div key={user.user_id} className="p-3 rounded-lg border bg-card">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium truncate max-w-[200px]">
                            {user.email || user.user_id.slice(0, 8)}
                          </span>
                          <Badge variant="outline" className="tabular-nums">
                            {user.total_usage} actions
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {user.features.slice(0, 5).map((feature) => {
                            const config = getFeatureConfig(feature.feature_name);
                            const Icon = config.icon;
                            return (
                              <Badge 
                                key={feature.feature_name} 
                                variant="secondary" 
                                className="text-xs gap-1"
                              >
                                <Icon className={`h-3 w-3 ${config.color}`} />
                                {feature.usage_count}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
